import React, { useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import maxBy from 'lodash/maxBy';
import orderBy from 'lodash/orderBy';
import get from 'lodash/get';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
} from 'recharts';
import { selectBarChartData, selectTextListData } from '../../../../utils';
import SplitContentContainer from '../SplitContentContainer/SplitContentContainer';
import SRLStudentResponses from '../SRLStudentResponses/SRLStudentResponses';
import TooltipBarChart from '../TooltipBarChart';
import styles from './SRLChallengesContent.module.css';

const TOOLTIP_CHARACTERS_PER_LINE = 48;
const BASE_TOOLTIP_HEIGHT = 60;
const LINE_HEIGHT = 25;
const TOOLTIP_X_OFFSET = 150;
const TOOLTIP_Y_OFFSET = 25;

const renderChallengesChart = (chartData, height) => {
  const [barGraphData, setBarGraphData] = useState({});

  let highDomainLimit = 40;
  const maxData = maxBy(chartData, 'student_count');
  if (maxData) {
    highDomainLimit = Number(maxData.student_count);
  }

  let formattedChartData = [];
  if (Array.isArray(chartData)) {
      // Pull out text after 1-5 so we just get number values
    formattedChartData = chartData.map((barData) => {
      const tooltipLabel = barData.tool_tip || barData.answer;
      return {
        answer: barData.display_text,
        student_count: parseInt(barData.student_count, 10),
        tooltipLabel,
        toolTipLineCount: Math.ceil(barData.answer.length / TOOLTIP_CHARACTERS_PER_LINE)
      };
    });

    // Add 'prefer not to say' students to the 'none' Bucket
    const preferData = formattedChartData.find(barData => barData.answer === 'Prefer Not to Say' || barData.answer === 'No Answer') || {};
    const noneData = formattedChartData.find(barData => barData.answer === 'No Challenges') || {};
    const preferCount = !isEmpty(preferData) ? parseInt(preferData.student_count) : 0;
    const noneCount = !isEmpty(noneData) ? parseInt(noneData.student_count) : 0;
    noneData.student_count = `${preferCount + noneCount}`;
    // Pull the Prefer data out
    const indexToRemove = formattedChartData.indexOf(preferData);
    if (indexToRemove > -1) {
      formattedChartData.splice(indexToRemove, 1);
    }
    formattedChartData = orderBy(formattedChartData, ['student_count'], ['desc']);
  }

  const handleOnMouseOver = (data) => {
    const lines = Math.ceil(data.payload.tooltipLabel.length / 50);
    const heightOffsets = { 1: 10, 2: 5, default: 0 };
    const extraOffset = heightOffsets[lines] || heightOffsets.default;
    const tooltipHeight = BASE_TOOLTIP_HEIGHT + (lines - 1) * LINE_HEIGHT + extraOffset;

    setBarGraphData({
      x: data.x,
      y: data.y - tooltipHeight
    });
  };

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartHeader}>Challenges</div>
      <ResponsiveContainer height={height}>
        <BarChart
          data={formattedChartData}
          margin={{ top: 0, right: 20, left: 40, bottom: 10 }}
          layout="vertical"
        >
          <ChartTooltip
            content={
              <TooltipBarChart>
                {(_label, payload) => <div className={styles.tooltipText}>{`${get(payload, '[0].payload.tooltipLabel', '')}`}</div>}
              </TooltipBarChart>
            }
            cursor={{ fill: 'transparent' }}
            allowEscapeViewBox={{ x: true, y: true }}
            position={{
              x: barGraphData.x - TOOLTIP_X_OFFSET,
              y: barGraphData.y + TOOLTIP_Y_OFFSET
            }}
            wrapperStyle={{
              visibility: !isEmpty(barGraphData) ? 'visible' : 'hidden',
              zIndex: 100,
              position: 'absolute'
            }}
          />
          <Bar
            dataKey="student_count"
            fill="var(--prs-c-info-800)"
            minPointSize={0}
            label={{
              value: 'student_count',
              position: 'right',
              fontFamily: 'Source Sans Pro',
              fontSize: 14,
              fill: '#242424'
            }}
            onMouseOver={handleOnMouseOver}
            onMouseLeave={() => setBarGraphData({})}
          />
          <YAxis
            dataKey="answer"
            type="category"
            tick={{
              fontSize: 14, width: 180,
            }}
            tickLine={false}
            interval={0}
          />
          <XAxis
            tick={false}
            axisLine={false}
            tickLine={false}
            domain={[0, highDomainLimit]}
            type="number"
            label={{ value: 'total students',
              position: 'center',
              fontSize: 14,
              fontFamily: 'Source Sans Pro',
              fontStyle: 'italic',
              fill: '#242424',
            }}
          />
          <CartesianGrid vertical={false} horizontal={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const renderStudentResponses = (studentResponses, isSRLIntroSurvey) => (
  <div className={styles.chartContainer}>
    {!isSRLIntroSurvey && <div className={styles.chartHeader}>Explanations</div>}
    <SRLStudentResponses
      studentResponses={studentResponses}
      responseLimit={isSRLIntroSurvey ? 3 : 2}
      lineLimit={isSRLIntroSurvey ? 3 : 4}
    />
  </div>
);

function SRLChallengesContent({ data, isMobileXS }) {
  const { insightsData, isSRLIntroSurvey } = data;

  const { studentResponses } = selectTextListData(insightsData);
  const explanations = studentResponses.length && renderStudentResponses(studentResponses, isSRLIntroSurvey);

  const chartData = !isSRLIntroSurvey && selectBarChartData(insightsData);
  const challengesChart = chartData && renderChallengesChart(chartData, 240);

  return (
    <SplitContentContainer
      leftColumnContent={challengesChart}
      rightColumnContent={explanations}
      borderPosition={'center'}
      stackedVersion={isMobileXS}
    />
  );
}

SRLChallengesContent.propTypes = {
  data: PropTypes.shape({
    insightsData: PropTypes.array.isRequired,
    isSRLIntroSurvey: PropTypes.bool,
  }),
  isMobileXS: PropTypes.bool,
};

export default SRLChallengesContent;
